import { RNG } from '../core/rng';
import { enemies } from '../entities/enemies';
import { P } from '../world/player';
import { groundYAt, losBlocked } from '../world/arena';
import { schedule, spawnRing } from './fx';
import { hurtPlayer } from './player';
import { spawnEnemy } from './spawn';
import { warnAura, warnBurst, warnCircle, warnLine } from '../render/warn-fx';
/* ============================================================
   攻擊載荷 —— 十三種 kind 跑完 28 隻小怪
   ============================================================

   `sim/enemy-ai.ts` 是狀態機（什麼時候動），這個檔是載荷（動了會怎樣）。
   拆兩個檔的理由不只是行數：**狀態機是 28 隻共用的一份，載荷是會一直長的**
   —— 之後 7 隻 Boss 的招式也會從這裡挑。

   分類軸是「玩家要做的反應」而不是「牠長什麼樣」，清單與四色對照見
   `data/enemy-stats.ts` 的 `ATK` 檔頭。

   ## 三條在這裡反覆出現的規則

   1. **紅色不加獨立傷害來源。** `lunge` 只給速度、`spin` 只暫時放大接觸判定，
      痛的都是撞到。紅色多一條彈道判定，玩家學到的「紅＝碰到才痛」就是錯的。
   2. **落點在預備開始那一刻就鎖定**（`e.tgx/tgz`），不是命中那一刻算。
      後者等於「地上的圈只是裝飾」，玩家躲了還是會中 —— 那比沒有預警更糟。
   3. **延遲發射一律走 `schedule()`**（吃模擬步的 dt），不是牆鐘計時器。
      而且回呼裡**不能碰 `e`** —— 那 0.8 秒之內牠可能已經死了、回池了、
      變成另一隻敵人了。要用的座標在排程當下就抄成數字（見 `stub`）。 */

/** 給 `eShoot` 用的假發射源。**排程回呼不能捉住敵人物件**（見檔頭第 3 點），
    而 `eShoot` 只讀 `pos.x/z`、`r`、`T.y`，所以抄這三個就夠。 */
function stub(x, z, r, y) { return { pos: { x: x, z: z }, r: r, T: { y: y } }; }

/** 半徑內的**其他**敵人套用一個效果。`mark` 與 `guard` 共用。

    加成掛在被加成者身上而不是「查附近有沒有施放者」，理由是後者要每隻每幀
    做一次鄰域查詢（420 隻 × 60fps），而前者只有施放的那一瞬間掃一次。 */
function aoeAllies(e, radius, fn) {
  var r2 = radius * radius, n = 0;
  for (var i = 0; i < enemies.length; i++) {
    var o = enemies[i];
    if (o === e || o.T.boss) continue;
    var dx = o.pos.x - e.pos.x, dz = o.pos.z - e.pos.z;
    if (dx * dx + dz * dz > r2) continue;
    fn(o); n++;
  }
  return n;
}

/** 曲射／空投的落點（x, z 交錯的扁平陣列）。**鎖定的是預備開始那一刻的
    玩家位置**，多發時平均散在半徑 `spread` 的圓上 ——
    圓周而不是隨機散布，是為了讓「往外跑」永遠是對的答案：
    隨機散布會有時候把安全區蓋掉，那條規則就學不起來了。 */
function impacts(e, A) {
  if (A.n <= 1) return [e.tgx, e.tgz];
  var out: number[] = [];
  for (var i = 0; i < A.n; i++) {
    var a = (i / A.n) * Math.PI * 2;
    out.push(e.tgx + Math.cos(a) * A.spread, e.tgz + Math.sin(a) * A.spread);
  }
  return out;
}

/** 預備開始的那一刻：鎖定落點、畫預警。

    **天藍不畫預警框**（規格 §9）——「有框＝那一下會傷你」才學得起來；
    支援型自己不傷你，它用不會被誤讀成威脅的畫法（只有邊、脈動、不收縮）。 */
export function telegraph(e, nx, nz) {
  var T = e.T, A = T.atk, hex = T.accent;
  e.tgx = P.pos.x; e.tgz = P.pos.z;
  var k = A.kind;

  if (k === 'cone') {
    warnCircle(e.pos.x + nx * T.rng * 0.55, e.pos.z + nz * T.rng * 0.55, T.rng * 0.5, T.tTel, hex);
  } else if (k === 'bolt' || k === 'sweep' || k === 'pull') {
    /* 直線類：一條指向目標的地面線。`pull` 是橙色（HAKEN）才畫；
       `wirbel` 走 `area`，它是紫色，畫圈。 */
    if (k === 'pull' && A.area) warnCircle(e.pos.x, e.pos.z, T.rng * 0.8, T.tTel, hex);
    else warnLine(e.pos.x, e.pos.z, nx, nz, T.rng * 1.35, T.tTel, hex);
  } else if (k === 'radial') {
    /* 七條線是噪音不是資訊 —— 對策是「貼近或拉遠」，所以畫的是自己腳下的圈。 */
    warnCircle(e.pos.x, e.pos.z, T.rng * 0.9, T.tTel, hex);
  } else if (k === 'jump') {
    /* 圈要撐到落地那一刻（`tTel + tAtk`），不然玩家會看到圈消失才被砸。 */
    warnCircle(e.tgx, e.tgz, A.radius, T.tTel + T.tAtk, hex);
  } else if (k === 'mortar') {
    var pts = impacts(e, A);
    for (var i = 0; i < pts.length; i += 2) {
      warnCircle(pts[i], pts[i + 1], A.radius, T.tTel + A.delay, hex);
    }
  } else if (k === 'fuse') {
    warnCircle(e.pos.x, e.pos.z, A.boom.r, T.tTel, hex);
  } else if (k === 'mark' || k === 'guard' || k === 'summon') {
    /* 只在施放者腳下畫一個小圈，**不畫真正的作用半徑**。
       實看抓到的：五隻 FÜHLER 的全尺寸光環就蓋滿整個畫面。
       使用者破例畫這個圈的理由是「不畫的話玩家不知道要先打誰」——
       那個目的用腳下的小圈就達成了，畫出精確範圍反而更難看出來。 */
    warnAura(e, Math.min((A.radius || 6) * 0.3, 3.0), T.tTel + T.tAtk, hex);
  }
  /* **撲擊與旋掃（紅）刻意不畫預警。** 實看抓到的：NADEL 佔生成權重的
     四成，場上 398 隻時每一隻都畫一條指向玩家的線 —— 畫面變成一個從四面
     八方指進來的星芒，而「往哪閃」沒有答案（每個方向都有線）。
     紅色的語彙本來就是「碰到才痛」，對策是走位不是閃某一條線。 */
}

/** 出手。`sh` 是 `sim/enemies.ts` 的 `eShoot`（傳進來而不是 import，
    避免 enemies ↔ enemy-ai 的循環 import）。 */
export function fire(e, nx, nz, sh) {
  var T = e.T, A = T.atk, dm = (e.buffDmg || 1) * e.dmgMul;
  var k = A.kind;

  if (k === 'lunge') {
    /* 撲擊不自己造成傷害 —— 紅色的傷害走接觸判定（`T.dmg`）。
       **倍率是 2.4 不是 3.4。** 3.4 × 7.13 = 24 單位/秒，比玩家的 11.5
       快一倍以上 —— 那不是「撲過來」是「鎖定命中」（壓力診斷 1.3 分陣亡）。 */
    e.vel.set(nx * T.spd * A.push, 0, nz * T.spd * A.push);
  } else if (k === 'spin') {
    /* 旋掃：把接觸判定放大到刃的長度，攻擊結束就收回來（`enemy-ai.ts`）。
       **沒有另開傷害來源** —— 橫刃是牠身體的延伸，痛的仍然是撞到。 */
    e.spinR = A.reach;
    e.vel.set(nx * T.spd * 0.6, 0, nz * T.spd * 0.6);
  } else if (k === 'bolt') {
    sh(e, nx, nz, 0, A.speed, A.dmg * dm);
    /* 點放：後續幾發抄座標排程，回呼裡不碰 `e`（檔頭第 3 點）。 */
    for (var b = 1; b < (A.burst || 1); b++) {
      queueShot(e, nx, nz, 0, A, dm, sh, b * A.gap);
    }
  } else if (k === 'sweep') {
    /* 掃射：整個攻擊窗口裡依序打出，角度從 −arc 掃到 +arc。
       一次全射出去就退化成 GEISSEL 的扇形了。 */
    for (var s = 0; s < A.n; s++) {
      var ang = -A.arc + (2 * A.arc) * (s / Math.max(1, A.n - 1));
      queueShot(e, nx, nz, ang, A, dm, sh, (s / A.n) * T.tAtk);
    }
  } else if (k === 'radial') {
    for (var q = 0; q < A.n; q++) {
      sh(e, nx, nz, (q / A.n) * Math.PI * 2, A.speed, A.dmg * dm);
    }
    spawnRing(e.pos.x, e.pos.z, T.accent, 0.6, T.rng * 0.9, 0.35);
  } else if (k === 'cone') {
    for (var c = 0; c < A.n; c++) {
      sh(e, nx, nz, (c - (A.n - 1) / 2) * A.spread, A.speed, A.dmg * dm);
    }
    /* 擴散環打在**預警圈的同一個位置**。這個配對就是預警語彙被學起來的方式：
       「剛才那個圈亮完 → 這裡炸了」重複幾次之後，圈本身就有意義了。 */
    warnBurst(e.pos.x + nx * T.rng * 0.55, e.pos.z + nz * T.rng * 0.55, T.rng * 0.5, T.accent);
  } else if (k === 'jump') {
    /* 跳過去。傷害排在落地那一刻（`tAtk` 之後）而不是起跳那一刻 ——
       起跳就結算的話玩家在圈裡看到的是「還沒砸到就痛了」。 */
    e.vel.set((e.tgx - e.pos.x) * A.push, 0, (e.tgz - e.pos.z) * A.push);
    queueBlast(e.tgx, e.tgz, A.radius, A.dmg * dm, T.accent, T.tAtk);
  } else if (k === 'mortar') {
    var pts = impacts(e, A), hex = T.accent, dmg = A.dmg * dm, rad = A.radius;
    for (var m = 0; m < pts.length; m += 2) {
      queueBlast(pts[m], pts[m + 1], rad, dmg, hex, A.delay);
    }
  } else if (k === 'fuse') {
    /* 自爆＝死亡。**不在這裡直接呼叫 `detonate`** —— 引爆與掉落、計分、
       回池是同一件事，各走各的會讓 DORN 炸完還站在原地。
       `killEnemy` 已經接了死亡殉爆（`T.boom`），所以這裡只要把血歸零，
       敵人迴圈下一行就會收掉它（在迴圈裡直接 splice 是既有的地雷）。 */
    e.hp = 0;
  } else if (k === 'pull') {
    /* 牽引不造成接觸傷害；它的威脅是把你拖進別人的射程。
       `area` 版（WIRBEL）在收束時多一次範圍傷害。 */
    e.pullT = A.dur;
    if (A.dmg) blast(e.pos.x, e.pos.z, T.rng * 0.8, A.dmg * dm, T.accent);
  } else if (k === 'mark') {
    aoeAllies(e, A.radius, function (o) { o.buffT = A.dur; o.buffDmg = A.dmg; o.buffSpd = A.spd; });
  } else if (k === 'guard') {
    aoeAllies(e, A.radius, function (o) { o.buffT = A.dur; o.buffArm = A.arm; });
  } else if (k === 'summon') {
    summon(e, A);
  }
}

/** 排一發延遲的子彈。抄下座標，回呼裡不碰敵人物件。 */
function queueShot(e, nx, nz, spread, A, dm, sh, delay) {
  var x = e.pos.x, z = e.pos.z, r = e.r, y = e.T.y;
  schedule(delay, function () { sh(stub(x, z, r, y), nx, nz, spread, A.speed, A.dmg * dm); });
}

/** 排一次延遲的落點爆炸。 */
function queueBlast(x, z, radius, dmg, hex, delay) {
  schedule(delay, function () { blast(x, z, radius, dmg, hex); });
}

/** 落點爆炸：**只打玩家不打敵人**（它是衝著玩家來的，不該幫玩家清場）。
    擴散環畫在預警圈的同一個位置 —— 那個配對就是預警語彙的教學。 */
function blast(x, z, radius, dmg, hex) {
  warnBurst(x, z, radius, hex);
  spawnRing(x, z, hex, 0.6, radius * 1.6, 0.35);
  var d = Math.hypot(P.pos.x - x, P.pos.z - z);
  // 地面爆炸的觸及高度跟著爆炸半徑走：小的踩不到空中的你，大的打得到
  if (d < radius && P.alive) hurtPlayer(dmg, groundYAt(x, z) + radius * 0.8);
}

/** 召喚（NEST）。**只生得出已經介紹過的東西** —— 生一隻玩家沒見過的敵人
    等於在第 16 分鐘辦一場「初次見面」，違反支柱一（每一種會致死的東西
    都要先講一次）。所以它固定生 NADEL：那是全場最早介紹、也最不需要
    重新學習的東西，而它的價值在數量不在種類。

    `spawnEnemy` 走的是 `sim/spawn.ts`，那個檔又 import 了 `enemy-ai` ——
    循環 import 在這裡是安全的（函式宣告會提升，實際呼叫發生在執行期），
    但**不要在這個檔的模組層級讀 spawn 的變數**。 */
function summon(e, A) {
  for (var i = 0; i < A.n; i++) {
    var a = RNG.next() * Math.PI * 2;
    spawnEnemy('nadel', e.pos.x + Math.cos(a) * A.dist, e.pos.z + Math.sin(a) * A.dist);
  }
  spawnRing(e.pos.x, e.pos.z, e.T.accent, 0.6, A.dist * 2.2, 0.45);
}

/** 牽引的每一步。放在這裡而不是狀態機裡，因為它是載荷的一部分：
    `pullT` 由 `fire()` 開始、由這裡收尾。 */
export function updatePull(e, dt) {
  if (!(e.pullT > 0)) return;
  e.pullT -= dt;
  var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz) || 0.001;
  /* 被地形擋住就拉不動 —— 躲掩體對橙色一律有效（規格 §2.3）。 */
  // 玩家在空中時視線越過構件（規格：空中沒有掩體）——遠程照打得到你
  if (losBlocked(e.pos.x, e.pos.z, P.pos.x, P.pos.z, P.y)) return;
  var f = e.T.atk.force * dt;
  P.vel.x += (dx / d) * f; P.vel.z += (dz / d) * f;
}

/** 攻擊結束時要還原的欄位。**池化物件的欄位沒清會出事**：
    下一隻普通的 BAR 會頂著兩倍的接觸判定跑出來，而且零錯誤（M8 記過一次）。 */
export function endAttack(e) { e.spinR = 0; }
